import { SvgParameterDc } from "@evergis/api";
import { ClassificationValues, RangeClass, UniqueClass } from "../../../interfaces/classification";
import {
  ParameterByAttribute,
  ParameterByAttributeValue,
  ParameterValue,
  ParameterValueWithSymbol,
  StrokeBrushType,
  FillBrushType,
  ClassifyAble,
  CalculatedParameter,
  CalculatedAble,
  UnidentifiedParameterValue,
} from "../../../interfaces/style";
import { isObject } from "../../../utils/isObject";
import { ClassificationCondition } from "../condition/ClassificationCondition";

const getRangeFromClassified = (values: ParameterByAttributeValue<ParameterValue>[]): RangeClass[] => {
  return values.map(({ condition, value, title }) => {
    const [from, to] = new ClassificationCondition(condition).range;

    return {
      from,
      to,
      title,
      parameterValue: value,
    };
  });
};

const getUniqueFromClassified = (values: ParameterByAttributeValue<ParameterValue>[]): UniqueClass[] => {
  return values.map(({ condition, value, title }) => {
    const { uniqueValue } = new ClassificationCondition(condition);

    return {
      uniqueValue,
      title,
      parameterValue: value,
    };
  });
};

const isUniqueValues = (values: ParameterByAttributeValue<ParameterValue>[]): boolean => {
  return values && new ClassificationCondition(values[0].condition).isUnique();
};

export const getClassesFromClassified = (values: ParameterByAttributeValue<ParameterValue>[]): ClassificationValues => {
  if (isUniqueValues(values)) {
    return getUniqueFromClassified(values);
  }

  return getRangeFromClassified(values);
};

export const getAttributeNameFromCondition = (condition: string): string | null =>
  new ClassificationCondition(condition).attributeName;

export const getAttributeNameFromClassified = (
  values: ParameterByAttributeValue<ParameterValueWithSymbol>[],
): string | null => (values?.length > 0 ? getAttributeNameFromCondition(values[0].condition) : null);

export const isStrokeParameter = (parameter: string, value: unknown): value is StrokeBrushType =>
  parameter.startsWith("stroke") && typeof value === "object";
export const isFillParameter = (parameter: string, value: unknown): value is FillBrushType =>
  parameter.startsWith("fill") && typeof value === "object";
export const isSvgFillParameter = (parameter: string, value: unknown): value is FillBrushType =>
  ["background", "figure"].includes(parameter) && typeof (value as SvgParameterDc)?.fillColor === "object";
export const isSvgStrokeParameter = (parameter: string, value: unknown): value is FillBrushType =>
  ["background", "figure"].includes(parameter) &&
  (typeof (value as SvgParameterDc)?.strokeColor === "object" ||
    typeof (value as SvgParameterDc)?.strokeWidth === "object");

export function isParameterByAttribute(
  value: UnidentifiedParameterValue,
): value is ParameterByAttribute<ParameterValue> {
  if (isObject(value) && value.type) {
    return ["byAttribute", "byScale"].includes(value.type);
  }

  return false;
}

export const isCalculatedParameter = (
  value: UnidentifiedParameterValue,
): value is CalculatedParameter<ParameterValue> => isObject(value) && value.type && value.type === "calculated";

export const getParameterValue = <Value extends ParameterValue>(
  value: Value | ClassifyAble<Value> | CalculatedAble<Value>,
): Value => {
  if (isParameterByAttribute(value) || isCalculatedParameter(value)) {
    return value.defaultValue;
  }

  return value;
};
